<template>
    <div>
        <h2 class="ix-page-title-secondary mt-4">Éco-participation</h2>

        <div class="form-group row">
            <label class="col-sm-4 col-form-label">Applicable</label>
            <div class="col-sm-8">
                <div class="custom-control custom-checkbox">
                    <input type="hidden" name="eco_applicable" value="0">
                    <input
                        type="checkbox"
                        class="custom-control-input"
                        name="eco_applicable"
                        id="eco_applicable"
                        value="1"
                        v-model="ecoApplicable"
                        @change="onEcoApplicableChange"
                    >
                    <label class="custom-control-label" for="eco_applicable">Cet article est soumis à l'éco-participation</label>
                </div>
            </div>
        </div>

        <div v-if="ecoApplicable" class="eco-section">
            <!-- Éco-participation manuelle (override) -->
            <div class="form-group row">
                <label for="eco_participation" class="col-sm-4 col-form-label">
                    Éco-participation fixe
                    <i class="far fa-info-circle text-info ml-1" v-b-tooltip.hover title="Si renseigné, désactive le calcul automatique"></i>
                </label>
                <div class="col-sm-8">
                    <div class="input-group">
                        <input
                            type="number"
                            step="0.0001"
                            min="0"
                            id="eco_participation"
                            name="eco_participation"
                            class="form-control"
                            v-model.number="ecoParticipationManual"
                            placeholder="Ex: 0.1500"
                            @input="onManualParticipationChange"
                        >
                        <div class="input-group-append">
                            <span class="input-group-text">€</span>
                        </div>
                    </div>
                    <small class="form-text text-muted">
                        <i class="far fa-exclamation-triangle text-warning"></i>
                        À utiliser uniquement si l'éco-participation a déjà été payée par un fournisseur français. Ce montant ne sera pas inclus dans les déclarations.
                    </small>
                </div>
            </div>

            <div v-if="!hasManualParticipation">
                <div class="form-group row">
                    <label for="eco_unit" class="col-sm-4 col-form-label">Unité de calcul <span class="text-danger">*</span></label>
                    <div class="col-sm-8">
                        <select
                            id="eco_unit"
                            name="eco_unit"
                            class="form-control"
                            v-model="ecoUnit"
                            required
                        >
                            <option value="">-- Choisissez une unité --</option>
                            <option value="unit">Par unité</option>
                            <option value="weight">Par poids</option>
                        </select>
                    </div>
                </div>

                <!-- Le poids se saisit dans l'identification de l'article : il sert aussi au transport. -->
                <div v-if="ecoUnit === 'weight'" class="form-group row">
                    <label class="col-sm-4 col-form-label">Poids unitaire <span class="text-danger">*</span></label>
                    <div class="col-sm-8">
                        <span v-if="weight" class="form-control-plaintext">{{ weight }} kg</span>
                        <span v-else class="form-control-plaintext text-danger">À renseigner dans l'identification</span>
                    </div>
                </div>

                <div class="form-group row">
                    <label for="eco_categories" class="col-sm-4 col-form-label">Catégories <span class="text-danger">*</span></label>
                    <div class="col-sm-8">
                        <div v-if="loading" class="alert alert-info">Chargement des catégories...</div>
                        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
                        <div v-else>
                            <!-- Filtre par filière -->
                            <div class="mb-3">
                                <label for="scheme_filter" class="d-flex align-items-center justify-content-between">
                                    <span>Filtrer par filière</span>
                                    <a v-if="canManageEcoSchemes" href="/bank/eco-schemes" target="_blank" class="text-muted small ml-2" title="Gérer les filières">
                                        <i class="far fa-link"></i> Gérer
                                    </a>
                                </label>
                                <select id="scheme_filter" class="form-control" v-model="schemeFilter">
                                    <option value="">Toutes les filières</option>
                                    <option v-for="scheme in schemes" :key="scheme.id" :value="scheme.id">
                                        {{ scheme.name }}
                                    </option>
                                </select>
                            </div>

                            <!-- Autocomplete multiselect -->
                            <label for="eco_categories" class="d-flex align-items-center justify-content-between">
                                <span>Catégories <span class="text-danger">*</span></span>
                                <a v-if="canManageEcoCategories" href="/bank/eco-categories" target="_blank" class="text-muted small ml-2" title="Gérer les catégories">
                                    <i class="far fa-link"></i> Gérer
                                </a>
                            </label>
                            <multiselect
                                v-model="selectedCategoriesObjects"
                                :options="filteredCategories"
                                :multiple="true"
                                :close-on-select="false"
                                :clear-on-select="false"
                                :preserve-search="true"
                                placeholder="Sélectionnez une ou plusieurs catégories"
                                label="display_label"
                                track-by="id"
                                :preselect-first="false"
                                select-label=""
                                selectedLabel="Sélectionné"
                                deselectLabel=""
                                @input="onCategoryChange"
                            >
                                <template slot="option" slot-scope="props">
                                    <div class="option__desc">
                                        <span class="option__title">{{ props.option.code }} - {{ props.option.label }}</span>
                                        <br>
                                        <small class="text-muted">
                                            <span v-if="props.option.eco_scheme">{{ props.option.eco_scheme.name }} • </span>
                                            {{ getCalculationModeLabel(props.option.calculation_mode) }}
                                            <span v-if="props.option.activeRate"> • {{ formatCurrency(props.option.activeRate.amount) }}</span>
                                            <span v-else class="text-warning"> • Aucun tarif</span>
                                        </small>
                                    </div>
                                </template>
                                <template slot="noResult">Aucune catégorie trouvée</template>
                                <template slot="noOptions">Aucune catégorie disponible</template>
                            </multiselect>

                            <small v-if="selectedCategoriesObjects.length > 0" class="form-text text-muted d-block mt-2">
                                {{ selectedCategoriesObjects.length }} catégorie(s) sélectionnée(s)
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Calculs dynamiques -->
                <div v-if="selectedCategoriesObjects.length > 0" class="form-group row">
                    <label class="col-sm-4 col-form-label">Calcul automatique</label>
                    <div class="col-sm-8">
                        <div class="alert alert-light">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Filière - Catégorie</th>
                                        <th class="text-right">Calcul</th>
                                        <th class="text-right">Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="category in selectedCategoriesObjects" :key="category.id">
                                        <td>
                                            <small class="text-muted">{{ category.eco_scheme ? category.eco_scheme.name : '' }}</small><br>
                                            <strong>{{ category.code }} - {{ category.label }}</strong>
                                        </td>
                                        <td class="text-right">
                                            <small class="text-muted">{{ getCalculationDetail(category) }}</small>
                                        </td>
                                        <td class="text-right">
                                            <span v-if="category.activeRate" class="badge badge-success">
                                                    {{ formatCurrency(calculateAmount(category), 4) }}
                                                </span>
                                            <span v-else class="badge badge-warning">Aucun tarif</span>
                                        </td>
                                    </tr>
                                    <tr class="table-active">
                                        <td colspan="2" class="text-right"><strong>Total éco-participation :</strong></td>
                                        <td class="text-right"><strong>{{ formatCurrency(totalEcoParticipation, 4) }}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="alert alert-info">
                <i class="far fa-info-circle"></i>
                L'éco-participation est fixée manuellement à <strong>{{ formatCurrency(ecoParticipationManual) }}</strong>. 
                La sélection des catégories est désactivée.
            </div>
        </div>

        <!-- Hidden inputs to preserve form data -->
        <input type="hidden" name="selected_eco_categories" :value="selectedCategories.join(',')">
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
    name: 'article-eco-tax-form',
    components: {
        Multiselect
    },
    props: {
        article: {
            type: Object,
            default: () => ({})
        },
        categoriesRoute: {
            type: String,
            required: true
        },
        canManageEcoSchemes: {
            type: Boolean,
            default: false
        },
        canManageEcoCategories: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            ecoApplicable: false,
            ecoUnit: '',
            weight: null,
            ecoParticipationManual: null,
            selectedCategories: [],
            selectedCategoriesObjects: [],
            categories: [],
            loading: true,
            error: null,
            schemeFilter: '',
            calculationModes: {
                'unit': 'Par unité',
                'weight': 'Par poids'
            }
        };
    },
    computed: {
        hasManualParticipation() {
            return this.ecoParticipationManual !== null && this.ecoParticipationManual !== '' && parseFloat(this.ecoParticipationManual) > 0;
        },
        schemes() {
            const uniqueSchemes = [];
            const schemeIds = new Set();
            
            this.categories.forEach(cat => {
                if (cat.eco_scheme && !schemeIds.has(cat.eco_scheme.id)) {
                    schemeIds.add(cat.eco_scheme.id);
                    uniqueSchemes.push(cat.eco_scheme);
                }
            });
            
            return uniqueSchemes.sort((a, b) => a.name.localeCompare(b.name));
        },
        filteredCategories() {
            let filtered = this.categories;
            
            if (this.schemeFilter) {
                filtered = filtered.filter(cat => cat.eco_scheme && cat.eco_scheme.id === parseInt(this.schemeFilter));
            }
            
            // Ajouter un label pour l'affichage
            return filtered.map(cat => ({
                ...cat,
                display_label: `${cat.code} - ${cat.label}`
            }));
        },
        totalEcoParticipation() {
            return this.selectedCategoriesObjects.reduce((total, category) => {
                return total + this.calculateAmount(category);
            }, 0);
        }
    },
    methods: {
        fetchCategories() {
            this.loading = true;
            this.error = null;
            return fetch(this.categoriesRoute, {
                ...{ method: 'GET' },
                ...this.getHttpJsonRequestHeaders()
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }
                    return response.json();
                })
                .then(data => {
                    this.categories = Array.isArray(data) ? data : [];
                    this.loading = false;
                    return data;
                })
                .catch(error => {
                    console.error('Error fetching categories:', error);
                    this.error = 'Erreur lors du chargement des catégories : ' + error.message;
                    this.loading = false;
                });
        },
        onEcoApplicableChange() {
            if (!this.ecoApplicable) {
                this.selectedCategories = [];
                this.selectedCategoriesObjects = [];
                this.ecoUnit = '';
                this.ecoParticipationManual = null;
            }
        },
        onManualParticipationChange() {
            if (this.hasManualParticipation) {
                // Si on fixe manuellement, on vide les catégories sélectionnées
                this.selectedCategories = [];
                this.selectedCategoriesObjects = [];
            }
        },
        onCategoryChange() {
            // Mettre à jour la liste des IDs
            this.selectedCategories = this.selectedCategoriesObjects.map(cat => cat.id);
            
            // Émettre l'événement pour le parent
            this.$emit('categories-changed', this.selectedCategories);
        },
        getCalculationModeLabel(mode) {
            return this.calculationModes[mode] || mode;
        },
        calculateAmount(category) {
            if (!category.activeRate) {
                return 0;
            }
            
            const rate = parseFloat(category.activeRate.amount);
            
            switch (category.calculation_mode) {
                case 'unit':
                    // Pour la fiche article, quantité = 1
                    return rate * 1;
                    
                case 'weight':
                    // Tarif × Poids × Quantité (1)
                    const weight = parseFloat(this.weight) || 0;
                    return rate * weight * 1;
                    
                default:
                    // Compatibilité legacy: l'ancien mode "fixed" est traité comme "unit".
                    return rate * 1;
            }
        },
        getCalculationDetail(category) {
            if (!category.activeRate) {
                return '-';
            }
            
            const rate = this.formatCurrency(category.activeRate.amount, 4);
            
            switch (category.calculation_mode) {
                case 'unit':
                    return `${rate} × 1`;
                    
                case 'weight':
                    const weight = parseFloat(this.weight) || 0;
                    return `${rate} × ${weight} kg × 1`;
                    
                default:
                    return `${rate} × 1`;
            }
        },
        formatCurrency(val, decimals = null) {
            const number = parseFloat(val) || 0;
            if (decimals !== null) {
                return new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: decimals
                }).format(number);
            }

            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR'
            }).format(number);
        }
    },
    mounted() {
        const form = this.$el.closest('form');
        const weightInput = form ? form.querySelector('input[name="weight"]') : null;
        if (weightInput) {
            this.weight = weightInput.value || null;
            weightInput.addEventListener('input', () => { this.weight = weightInput.value || null; });
        }

        // Load article data if editing
        if (this.article && this.article.eco_applicable) {
            this.ecoApplicable = true;
            this.ecoUnit = this.article.eco_unit === 'fixed' ? 'unit' : (this.article.eco_unit || '');
            this.ecoParticipationManual = this.article.eco_participation || null;
        }

        // Load selected categories (supports both camelCase and snake_case)
        const articleCategories = this.article?.ecoCategories || this.article?.eco_categories || [];
        if (Array.isArray(articleCategories)) {
            this.selectedCategories = articleCategories
                .map(cat => cat && cat.id)
                .filter(id => id !== null && id !== undefined)
                .map(id => String(id));
        }

        // Load categories
        this.fetchCategories().then(() => {
            // Une fois les catégories chargées, mapper les IDs vers les objets
            if (this.selectedCategories.length > 0) {
                const selectedCategoryIds = new Set(this.selectedCategories.map(id => String(id)));
                this.selectedCategoriesObjects = this.categories
                    .filter(cat => selectedCategoryIds.has(String(cat.id)))
                    .map(cat => ({
                        ...cat,
                        display_label: `${cat.code} - ${cat.label}`
                    }));
            }
        });
    }
};
</script>
